import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);

  const comic = useMemo(() => comics.find((c) => c.id === comicId), [comicId]);

  if (!comic) {
    return <Navigate to="/" replace />;
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画在线阅读 - 韩漫屋`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫屋',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanwu.com/article/${comic.id}`,
    },
  };

  return (
    <div className="pt-16">
      <PageSEO
        title={comic.title}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。韩漫屋免费韩国漫画在线阅读，${comic.author}作品，${comic.status}中。`}
        keywords={`${comic.title},${comic.category}韩漫,韩国漫画,韩漫屋`}
        canonicalUrl={`https://hanmanwu.com/article/${comic.id}`}
        ogType="article"
        schemaJson={comicSchema}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '漫画分类', path: '/category/recommend' },
          ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
          { label: comic.title },
        ]}
      />

      <article className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          <div className="flex-shrink-0 w-full sm:w-[260px] lg:w-[300px] mx-auto lg:mx-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩漫屋免费韩国漫画`}
                title={`${comic.title} - 韩国漫画在线阅读`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
              {comic.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">
              作者：<strong>{comic.author}</strong>
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                {comic.status}
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-star-fill text-accent-400 w-4 h-4 flex items-center justify-center"></i>
                {comic.rating} 分
              </span>
              <span className="text-sm text-foreground-600 flex items-center gap-1">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {(comic.views / 10000).toFixed(1)} 万阅读
              </span>
              <span className="text-sm text-foreground-600">
                更新于 {comic.updateDate}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-5">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 text-xs rounded bg-accent-100 text-accent-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
              <h2 className="font-heading text-base font-semibold text-foreground-800 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
            </div>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
            章节列表
          </h2>
          <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
            {comic.chapters.map((chapter, index) => (
              <div
                key={chapter.id}
                className={`flex items-center justify-between px-4 md:px-5 py-3.5 ${
                  index < comic.chapters.length - 1 ? 'border-b border-background-200/50' : ''
                } hover:bg-background-50/50 transition-colors cursor-pointer`}
              >
                <span className="text-sm text-foreground-700 truncate mr-3">
                  {chapter.title}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap flex-shrink-0">
                  {chapter.updateDate}
                </span>
              </div>
            ))}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}